import { useEffect, useState } from "react";

/**
 * SectionJumpNav — sticky horizontal jump bar that appears once the user
 * has scrolled past the trigger element (the Pricing CTA, by default).
 *
 * Smooth-scrolls to a section by id when a pill is clicked.
 *
 * Props:
 *   items    — array of { id: 'section-id', label: 'Display' }
 *   triggerId — element id whose bottom edge triggers the nav to appear
 */
export default function SectionJumpNav({ items, triggerId = "pricing-cta" }) {
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Show the jump nav when the trigger section is past the top of the viewport
  useEffect(() => {
    const trigger = document.getElementById(triggerId);
    if (!trigger) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          // When trigger leaves the top of the viewport going up → show jump nav
          if (e.boundingClientRect.top < 0) setVisible(true);
          else setVisible(false);
        }
      },
      { threshold: 0, rootMargin: "0px 0px -90% 0px" },
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [triggerId]);

  // Track which collapsible section is currently in view
  useEffect(() => {
    if (!visible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.find((e) => e.isIntersecting);
        if (intersecting) setActiveId(intersecting.target.id);
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    for (const it of items) {
      const el = document.getElementById(it.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [visible, items]);

  const handleJump = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Open the section if it's a CollapsibleSection in collapsed state
    const toggle = el.querySelector(`[data-testid="${el.getAttribute("data-testid")}-toggle"]`);
    if (toggle && toggle.getAttribute("aria-expanded") === "false") {
      toggle.click();
    }
    // smooth-scroll after the section expands (slight delay for the transition)
    setTimeout(() => {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 110;
      window.scrollTo({ top, behavior: "smooth" });
    }, toggle && toggle.getAttribute("aria-expanded") === "false" ? 80 : 0);
  };

  return (
    <div
      data-testid="landing-jump-nav"
      className={`sticky top-16 z-40 border-b border-zinc-200/70 bg-white/85 backdrop-blur-md transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none h-0 overflow-hidden border-b-0"
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 lg:px-6 py-2.5 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 shrink-0 mr-2">
          Jump to ◆
        </span>
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => handleJump(it.id)}
            data-testid={`jump-${it.id}`}
            className={`shrink-0 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors border ${
              activeId === it.id
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900 hover:text-zinc-900"
            }`}
          >
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}
